import TableSkeletonElement from 'Components/GMS/Products/components/TableSkeletonElement';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import TdElement from '../ComputedSubstitutions/components/TdElement';
import { formatDateDDMMMYYYY } from 'Common/HelperClass';
import { ChangeIcon, CopyIcon, DeleteIcon, EditIcon, OverFlowIcon, WarningIcon } from 'assests/GmsIcons';
import OverflowOption from '../ComputedSubstitutions/components/OverflowOption';
import ToggleButton from 'Components/Common/components/Inputs/ToggleButton';
import { ArsContext } from '../ARS/Contexts/ArsContext';
import ChangeOwnerDialog, { User } from './ChangeOwnerDialog';
import { toast } from 'react-toastify';
import { transferOwner } from './services/RuleService';
import { AppContext } from 'Context/AppContext';
import ReactDOM from 'react-dom';
import { RuleSortableColumn, SortType } from './enums';

function RuleTable({ mode, ruleList, loading, handleDelete, handleEditAndCreateVersion, NoDataComponent, handleActiveToggle, isSidePanel, handleClick, fetchTableData, sortColumn, setSortColumn, sortDirection, setSortDirection }: RuleTableProps) {
    const [selectIndex, setSelectIndex] = useState<SelectIndex>({ defaultIndex: null, versionIndex: null });
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const menuRef = useRef<HTMLDivElement | null>(null);
    const { model, activeSubmodelIndex } = useContext(ArsContext);
    const [showDialog, setShowDialog] = useState<boolean>(false);
    const [selectedRule, setSelectedRule] = useState<Rule | null>(null);
    const appData = useContext(AppContext);

    const rulesArray = useMemo(() => {
        const submodel = model?.submodel[activeSubmodelIndex];
        return mode === 'pure_compatibility'
            ? submodel?.pure_compatibility_rules
            : submodel?.rules;
    }, [mode, model, activeSubmodelIndex]);

    const closeOwnerChangeDialog = () => setShowDialog(false);

    const onClickedChangeOwner = (responseItem: Rule) => {
        setShowDialog(true);
        setSelectedRule(responseItem);
    }

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setSelectIndex({ defaultIndex: null, versionIndex: null });
            }
        }
        function handleScroll() {
            if (selectIndex.defaultIndex !== null || selectIndex.versionIndex !== null) {
                setSelectIndex({ defaultIndex: null, versionIndex: null });
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('scroll', handleScroll, true);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [selectIndex]);

    const handleOverflowClick = (event: React.MouseEvent<HTMLDivElement>, defaultIndex: number, versionIndex: number | null) => {
        event.stopPropagation();
        const rect = event.currentTarget.getBoundingClientRect();
        setMenuPosition({
            top: rect.bottom - window.scrollY,
            left: rect.left - window.scrollX,
        });
        setSelectIndex({ defaultIndex, versionIndex });
    };


    const handleSort = (column: RuleSortableColumn) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === SortType.ASC ? SortType.DESC : SortType.ASC);
        } else {
            setSortColumn(column);
            setSortDirection(SortType.ASC);
        }
    };

    const deleteFunction = async (rule: Rule) => {
        handleDelete(rule)
    }

    const sortElement = (column: string) => {
        return sortColumn === column ? (
            <span>
                {sortDirection === "ASC" ? (
                    <i className="fas fa-sort-up" />
                ) : (
                    <i className="fas fa-sort-down" />
                )}
            </span>
        ) : (
            <i className="fas fa-sort" />
        );
    };

    const hasRuleOwnerTransferPermission = () => (appData?.pm?.checkPermission("gmsrules_transfer"))

    if (loading) { return <div className=""><TableSkeletonElement /></div> }

    if (loading === false && ruleList?.length === 0) { return <div><NoDataComponent /></div> }

    const headerClasses = "tw-px-6 tw-cursor-pointer tw-py-3 tw-text-left tw-text-sm tw-font-semibold tw-text-[#6B778C] tw-tracking-wider tw-whitespace-nowrap";

    const getToggleButtonValue = (rule: Rule): boolean => {
        return rulesArray?.includes(rule?.id) ?? false;
    };

    const ruleChangeSuccessMessage = (user: string) => `Rule owner changed to ${user}. They can now edit and manage this rule.`;
    const ruleChangeFailureMessage = "Rule owner could not be changed";

    const onRuleOwnerChange = async (data: TransferOwnerPayload) => {
        try {
            const response = await transferOwner(data)
            if (response?.data?.done)
                toast.success(ruleChangeSuccessMessage(`${data?.username}`));
            else
                toast.error(ruleChangeFailureMessage)
        } catch (error) {
            console.log('Error', error);
            toast.error(ruleChangeFailureMessage)
        }
        finally {
            closeOwnerChangeDialog()
            fetchTableData()
        }
    }

    return (<>
        {isSidePanel && (<div className="tw-w-[97%] tw-h-7 tw-bg-[#fffae6] tw-border tw-my-2 tw-mx-4 tw-border-[#ffc400] tw-flex tw-items-center tw-px-4 tw-py-1"><WarningIcon /><p className="tw-ml-2">Note: Only committed rule versions are displayed.</p></div>)}
        <div className="tw-border tw-border-solid tw-mt-3 tw-border-[#DFE0E6] tw-border-r-2 ">
            <div className="table-container" style={{ width: '100%', height: '600px', overflowX: 'auto' }}>
                <table className="tw-bg-[#DFE1E6] tw-w-screen"> {/* fixing width as the table container has height and width for scroll*/}
                    <thead className="tw-sticky tw-top-0 tw-bg-[#DFE1E6]"><tr>
                        <th key="Rule Name" className={`${headerClasses} tw-sticky tw-left-0 tw-bg-[#DFE1E6]`} onClick={() => handleSort(RuleSortableColumn.NAME)}><div className={`tw-flex tw-items-center tw-ml-2`}><span>Rule Name</span><span className="tw-ml-1">{sortElement(RuleSortableColumn.NAME)}</span></div></th>
                        {isSidePanel && <th key="Active" className={headerClasses}><div className="tw-flex tw-items-center tw-z-40"><span>Active</span></div></th>}
                        <th key="Category 1" className={headerClasses} onClick={() => handleSort(RuleSortableColumn.CATEGORY_1)}><div className="tw-flex tw-items-center"><span>Category 1</span><span className="tw-ml-1">{sortElement(RuleSortableColumn.CATEGORY_1)}</span></div></th>
                        <th key="Category 2" className={headerClasses} onClick={() => handleSort(RuleSortableColumn.CATEGORY_2)}><div className="tw-flex tw-items-center"><span>Category 2</span><span className="tw-ml-1">{sortElement(RuleSortableColumn.CATEGORY_2)}</span></div></th>
                        <th key="Type" className={headerClasses} onClick={() => handleSort(RuleSortableColumn.TYPE)}><div className="tw-flex tw-items-center"><span>Type</span><span className="tw-ml-1">{sortElement(RuleSortableColumn.TYPE)}</span></div></th>
                        <th key="Created on" className={headerClasses} onClick={() => handleSort(RuleSortableColumn.CREATED_ON)}><div className="tw-flex tw-items-center"><span>Created On</span><span className="tw-ml-1">{sortElement(RuleSortableColumn.CREATED_ON)}</span></div></th>
                        <th key="Updated by" className={headerClasses} onClick={() => handleSort(RuleSortableColumn.UPDATED_BY)}><div className="tw-flex tw-items-center"><span>Updated By</span><span className="tw-ml-1">{sortElement(RuleSortableColumn.UPDATED_BY)}</span></div></th>
                        {!isSidePanel && <th key="Committed" className={headerClasses} onClick={() => handleSort(RuleSortableColumn.COMMIT)}><div className="tw-flex tw-items-center"><span>Committed</span><span className="tw-ml-1">{sortElement(RuleSortableColumn.COMMIT)}</span></div></th>}
                        <th key="Last Updated" className={headerClasses} onClick={() => handleSort(RuleSortableColumn.LAST_UPDATED)}><div className="tw-flex tw-items-center"><span>Last Updated</span><span className="tw-ml-1">{sortElement(RuleSortableColumn.LAST_UPDATED)}</span></div></th>
                        {!isSidePanel && <th className="table-header-style tw-sticky tw-right-0 tw-bg-[#DFE1E6] tw-z-40"></th>}
                    </tr></thead>
                    <tbody className="bg-white divide-y divide-gray-200 tw-z-10 tw-relative">
                        {
                            ruleList?.map((responseItem, defaultIndex) => {
                                return <>
                                    {
                                        <tr key={responseItem?.id} className="hover:bg-gray-100">
                                            {TdElement({ defaultIndex: defaultIndex, versionIndex: null }, <div className="tw-text-blue-700 tw-text-sm  tw-leading-tight tw-ml-6 tw-mb tw-cursor-pointer" onClick={() => handleClick(responseItem)}>{responseItem?.name ?? ""}</div>, true, true)}
                                            {isSidePanel && TdElement({ defaultIndex: defaultIndex, versionIndex: null }, <ToggleButton name={`radio-group-${defaultIndex}`} disabled={false} value={getToggleButtonValue(responseItem)} onChange={() => handleActiveToggle(responseItem, !getToggleButtonValue(responseItem))}></ToggleButton>)}
                                            {TdElement({ defaultIndex: defaultIndex, versionIndex: null }, responseItem?.left_category_name)}
                                            {TdElement({ defaultIndex: defaultIndex, versionIndex: null }, responseItem?.right_category_name)}
                                            {TdElement({ defaultIndex: defaultIndex, versionIndex: null }, responseItem?.rule_type)}
                                            {TdElement({ defaultIndex: defaultIndex, versionIndex: null }, formatDateDDMMMYYYY(responseItem?.created_at) || "")}
                                            {TdElement({ defaultIndex: defaultIndex, versionIndex: null }, (responseItem?.updated_by_username || responseItem?.created_by_username) ?? '')}
                                            {!isSidePanel && TdElement({ defaultIndex: defaultIndex, versionIndex: null }, <div style={{ backgroundColor: responseItem?.commit === true ? '#CFFFCE' : '#E9E9EF' }} className={`tw-px-3.5 tw-py-1  tw-rounded-[14px] tw-justify-start tw-items-center tw-gap-1 tw-inline-flex`}>
                                                <label style={{ color: responseItem.commit === true ? '#11980C' : '#5C5F62' }} className={`tw-text-xs`}>{responseItem.commit === true ? "Yes" : "No"}</label>
                                            </div>)}
                                            {TdElement({ defaultIndex: defaultIndex, versionIndex: null }, formatDateDDMMMYYYY(responseItem?.updated_at) || "")}
                                            {!isSidePanel && TdElement({ defaultIndex: defaultIndex, versionIndex: null },
                                                <>
                                                    <div className="tw-cursor-pointer tw-py-2 tw-rounded-lg hover:tw-bg-shades-of-grey-20 tw-sticky"
                                                        onClick={(e) => {
                                                            if (defaultIndex === selectIndex.defaultIndex && selectIndex.versionIndex === null) setSelectIndex({ defaultIndex: null, versionIndex: null });
                                                            else { handleOverflowClick(e, defaultIndex, null); }
                                                        }}>
                                                        <OverFlowIcon />
                                                    </div>

                                                    {defaultIndex === selectIndex.defaultIndex && selectIndex.versionIndex === null ?
                                                        ReactDOM.createPortal(
                                                            <div ref={menuRef}
                                                                className="tw-fixed tw-rounded-[4px] tw-bg-white tw-ring-1 tw-ring-[#EBECF0]"
                                                                style={{
                                                                    top: `${menuPosition.top}px`,
                                                                    left: `${menuPosition.left + 40}px`,
                                                                    zIndex: 9999
                                                                }}>
                                                                <div className={`tw-origin-top-left tw-mr-8 tw-right-0 tw-absolute tw-mx-3 tw-rounded-[4px] tw-bg-white tw-ring-1 tw-ring-[#EBECF0]`}
                                                                    style={{ zIndex: 9999 }}>
                                                                    {OverflowOption(responseItem, <EditIcon />, "Edit", () => { setSelectIndex({ defaultIndex: null, versionIndex: null }); handleEditAndCreateVersion(responseItem, "edit") })}
                                                                    {OverflowOption(responseItem, <CopyIcon />, "Copy", () => { setSelectIndex({ defaultIndex: null, versionIndex: null }); handleEditAndCreateVersion(responseItem, "copy") })}
                                                                    {hasRuleOwnerTransferPermission() && OverflowOption(responseItem, <ChangeIcon />, "Change Owner", () => { setSelectIndex({ defaultIndex: null, versionIndex: null }); onClickedChangeOwner(responseItem) })}
                                                                    {OverflowOption(responseItem, <DeleteIcon />, "Delete", () => { setSelectIndex({ defaultIndex: null, versionIndex: null }); deleteFunction(responseItem) })}
                                                                </div>
                                                            </div>,
                                                            document.body
                                                        )
                                                        : null}
                                                </>, false, false, true
                                            )}
                                        </tr>
                                    }
                                </>
                            })
                        }
                        {showDialog ? <ChangeOwnerDialog dialogHeader={"Change Owner"} labelName={"Select owner"} onClose={() => { closeOwnerChangeDialog(); setSelectedRule(null) }} onSubmit={(user: User) => { if (selectedRule != null) onRuleOwnerChange({ rule_id: selectedRule?.id, userid: user.id, username: `${user?.first_name} ${user?.last_name}` }) }} /> : null}
                    </tbody>
                </table>
            </div>
        </div>
    </>);
}


export default RuleTable